import type { RouteLocationNormalizedLoaded } from 'vue-router'

import type { ProjectRouteIdentity } from '~/shared/lib'
import { useAuth } from '~/entities/auth'
import {
  getProjectRouteParam,
  isUuid,

  replaceProjectRefInPath,
  resolveProjectQueryVariables,
} from '~/shared/lib'

import { useProjectRouteStore } from './projectRouteStore'
import { fetchProjectByRouteRef } from './resolveProjectByRouteRef'

export function useProjectRouteRef(route = useRoute(), router = useRouter()) {
  const projectRouteStore = useProjectRouteStore()
  const { isAuthenticated } = useAuth()

  const projectRef = computed(() => (route.params.id as string | undefined)?.trim() ?? '')
  const projectQueryVariables = computed(() => resolveProjectQueryVariables(projectRef.value))

  const projectId = ref('')
  const projectSlug = ref<string | null>(null)
  const isResolving = ref(false)
  const resolveError = ref<Error | null>(null)

  async function resolveRouteRef() {
    const ref = projectRef.value
    if (!ref) {
      projectId.value = ''
      projectSlug.value = null
      projectRouteStore.setResolved(null)
      return
    }

    const cached = projectRouteStore.current
    if (cached?.projectRef === ref && cached.projectId) {
      projectId.value = cached.projectId
      projectSlug.value = cached.projectSlug ?? null
      resolveError.value = null
      isResolving.value = false
      return
    }

    if (isUuid(ref)) {
      projectId.value = ref
    }

    isResolving.value = true
    resolveError.value = null

    try {
      const resolved = await fetchProjectByRouteRef(ref, {
        usePublicQuery: !isAuthenticated.value,
      })
      if (!resolved) {
        resolveError.value = new Error('Project not found')
        projectId.value = isUuid(ref) ? ref : ''
        projectSlug.value = null
        projectRouteStore.setResolved(null)
        return
      }

      projectId.value = resolved.projectId
      projectSlug.value = resolved.projectSlug
      projectRouteStore.setResolved(resolved)

      const canonicalRef = getProjectRouteParam({
        id: resolved.projectId,
        slug: resolved.projectSlug,
      })

      if (canonicalRef !== ref) {
        projectRouteStore.markSupersededProjectRouteRef(ref)
        const nextPath = replaceProjectRefInPath(route.fullPath, canonicalRef)
        if (nextPath !== route.fullPath) {
          await router.replace(nextPath)
        }
      }
    }
    catch (error) {
      resolveError.value = error instanceof Error ? error : new Error(String(error))
    }
    finally {
      isResolving.value = false
    }
  }

  watch(
    [projectRef, isAuthenticated],
    () => {
      void resolveRouteRef()
    },
    { immediate: true },
  )

  function syncProjectRouteIdentity(identity: ProjectRouteIdentity) {
    const slug = identity.slug?.trim() || null
    const nextRef = getProjectRouteParam(identity)
    const prev = projectRouteStore.current

    if (prev?.projectId === identity.id && prev.projectRef && prev.projectRef !== nextRef) {
      projectRouteStore.markSupersededProjectRouteRef(prev.projectRef)
    }

    if (projectRef.value && projectRef.value !== nextRef) {
      projectRouteStore.markSupersededProjectRouteRef(projectRef.value)
    }

    projectId.value = identity.id
    projectSlug.value = slug

    projectRouteStore.setResolved({
      projectRef: nextRef,
      projectId: identity.id,
      projectSlug: slug,
      accessContext: prev?.projectId === identity.id
        ? prev.accessContext
        : { admins: [], ownerId: '' },
    })
  }

  const routeParamForLinks = computed(() => {
    const fromResolved = getProjectRouteParam({
      id: projectId.value,
      slug: projectSlug.value,
    })

    if (fromResolved) {
      return fromResolved
    }

    const ref = projectRef.value
    if (ref && !isUuid(ref)) {
      return ref
    }

    return ref
  })

  return {
    projectRef,
    projectId: computed(() => projectId.value),
    projectSlug: computed(() => projectSlug.value),
    projectQueryVariables,
    routeParamForLinks,
    isResolving: computed(() => isResolving.value),
    resolveError: computed(() => resolveError.value),
    resolveRouteRef,
    syncProjectRouteIdentity,
  }
}

export function useResolvedProjectIdFromRoute(route: RouteLocationNormalizedLoaded = useRoute()) {
  const { projectId, isResolving } = useProjectRouteRef(route)
  return { projectId, isResolving }
}
